import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";
import { base64ToFile } from "@/common/utils/files";
import { getDate } from "@/common/utils/dates";

import {
  equipmentAdd,
  equipmentDel,
  equipmentList,
  equipmentModelCSV,
  equipmentUpload,
} from "@/common/api/rest/equipment";

import type { FileType } from "@/common/types/common";
import type {
  EquipmentListType,
  EquipmentPayloadType,
} from "@/common/types/equipment";

import { RegisterTheme } from "@/common/themes";
import { equipmentForm, fileUploadForm } from "@/components";
import {
  baseAvatar,
  baseConfirm,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const { t, tm } = useI18n();
const route = useRoute();
const router = useRouter();

const accountId = computed(() => route.params.id as string);

const tableHeader = computed(() => [
  { key: "avatar", label: null },
  { key: "code", label: t("common.labels.code") },
  { key: "name", label: t("common.labels.name") },
  { key: "status", label: t("common.labels.status") },
  { key: "calibration_date", label: t("common.labels.calibration_date") },
  { key: "next_calibration", label: t("common.labels.next_calibration") },
]);

const tableData = ref<EquipmentListType[]>([]);

const selected = ref<EquipmentListType | null>(null);
const payload = ref<EquipmentPayloadType | null>(null);

const fileUpload = ref<FileType | null>(null);
const fileExtensions = ref(["csv"]);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModal = ref(false);
const showModalUpload = ref(false);
const showConfirmDel = ref(false);

const ItemStatusEnum = computed(
  () => tm("enums.item_status") as Record<string, string>,
);

function fetchData() {
  equipmentList(accountId.value, {
    limit: limit.value,
    page: page.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updatedPayload(data: Partial<EquipmentPayloadType>) {
  payload.value = { ...payload.value, ...data } as any;
}

function add() {
  showModal.value = true;
  payload.value = null;
}

function addCancel() {
  showModal.value = false;
  payload.value = null;
}

function addConfirm() {
  if (!payload.value) return;

  store.commit("SET_LOADING", true);
  showModal.value = false;

  equipmentAdd(accountId.value, payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        router.push(
          `/${accountId.value}/register/equipment/edit/${data.equipment_id}`,
        );
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function edit(data: EquipmentListType) {
  router.push(
    `/${accountId.value}/register/equipment/edit/${data.equipment_id}`,
  );
}

function remove(data: EquipmentListType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function removeCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function removeConfirm() {
  if (!selected.value) return;

  store.commit("SET_LOADING", true);
  showConfirmDel.value = false;

  equipmentDel(accountId.value, selected.value.equipment_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function searchItem(value: string) {
  page.value = 0;
  search.value = value;
  fetchData();
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function uploadCsv() {
  showModalUpload.value = true;
}

function uploadCancel() {
  showModalUpload.value = false;
  fileUpload.value = null;
}

function uploadConfirm() {
  if (!fileUpload.value) return;

  showModalUpload.value = false;
  store.commit("SET_LOADING", true);

  equipmentUpload(accountId.value, fileUpload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        uploadCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function downloadModel() {
  equipmentModelCSV(accountId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        base64ToFile(data.file, data.name, data.type);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(() => {
  fetchData();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(RegisterTheme), { "tab-active": "equipment" }, {
    title: _withCtx(() => [
      _createVNode(_unref(svgIcon), {
        icon: "equipment",
        size: "2rem"
      }),
      _createTextVNode(" " + _toDisplayString(_unref(t)("common.labels.equipments")), 1)
    ]),
    content: _withCtx(() => [
      _createVNode(_unref(baseTable), {
        header: tableHeader.value,
        data: tableData.value,
        limit: limit.value,
        page: page.value,
        pages: pages.value,
        total: total.value,
        searched: searched.value,
        options: true,
        edit: true,
        del: true,
        upload: true,
        "onChange:page": changePage,
        onSearching: searchItem,
        "onBtn:add": _cache[0] || (_cache[0] = ($event: any) => (add())),
        "onBtn:edit": edit,
        "onBtn:del": remove,
        "onBtn:upload": _cache[1] || (_cache[1] = ($event: any) => (uploadCsv()))
      }, {
        avatar: _withCtx(({ data }) => [
          _createVNode(_unref(baseAvatar), {
            url: data.avatar?.small,
            size: "1.75rem",
            background: "#ffffff"
          }, null, 8, ["url"])
        ]),
        status: _withCtx(({ data }) => [
          _createTextVNode(_toDisplayString(ItemStatusEnum.value[data.status]), 1)
        ]),
        calibration_date: _withCtx(({ data }) => [
          _createTextVNode(_toDisplayString(_unref(getDate)(data.calibration_date)), 1)
        ]),
        next_calibration: _withCtx(({ data }) => [
          _createTextVNode(_toDisplayString(_unref(getDate)(data.next_calibration)), 1)
        ]),
        _: 1
      }, 8, ["header", "data", "limit", "page", "pages", "total", "searched"]),
      _createVNode(_unref(baseModal), {
        show: showModal.value,
        title: 
          _unref(t)('common.labels.add', {
            concat: _unref(t)('common.labels.equipment'),
          }).toUpperCase()
        ,
        "show-button-confirm": true,
        "button-confirm": _unref(t)('common.labels.add'),
        "button-close": _unref(t)('common.labels.cancel'),
        onClose: _cache[2] || (_cache[2] = ($event: any) => (addCancel())),
        onConfirm: _cache[3] || (_cache[3] = ($event: any) => (addConfirm()))
      }, {
        content: _withCtx(() => [
          _createVNode(_unref(equipmentForm), {
            data: payload.value,
            "is-patch": false,
            onUpdateData: updatedPayload
          }, null, 8, ["data"])
        ]),
        _: 1
      }, 8, ["show", "title", "button-confirm", "button-close"]),
      _createVNode(_unref(baseModal), {
        show: showModalUpload.value,
        title: 
          _unref(t)('common.labels.upload', {
            concat: _unref(t)('common.labels.equipment'),
          }).toUpperCase()
        ,
        "show-button-confirm": true,
        "button-confirm": _unref(t)('common.labels.send'),
        "button-close": _unref(t)('common.labels.cancel'),
        onClose: _cache[6] || (_cache[6] = ($event: any) => (uploadCancel())),
        onConfirm: _cache[7] || (_cache[7] = ($event: any) => (uploadConfirm()))
      }, {
        content: _withCtx(() => [
          _createVNode(_unref(fileUploadForm), {
            extensions: fileExtensions.value,
            model: true,
            onUpdateData: _cache[4] || (_cache[4] = ($event: any) => (fileUpload.value = $event)),
            onModelDownload: _cache[5] || (_cache[5] = ($event: any) => (downloadModel()))
          }, null, 8, ["extensions"])
        ]),
        _: 1
      }, 8, ["show", "title", "button-confirm", "button-close"]),
      _createVNode(_unref(baseConfirm), {
        show: showConfirmDel.value,
        title: 
          _unref(t)('common.labels.remove', {
            concat: _unref(t)('common.labels.sample'),
          }).toUpperCase()
        ,
        message: 
          _unref(t)('common.texts.remove_message', {
            concat: selected.value?.code,
          })
        ,
        onCancel: removeCancel,
        onConfirm: removeConfirm
      }, null, 8, ["show", "title", "message"])
    ]),
    _: 1
  }))
}
}

})